import { SessionState } from 'common/store/session/reducer';
import i18nService from 'common/services/i18nService';
import PlayerRequestBuilder, { PlayerParams, PreviewMode } from 'common/components/player/PlayerRequestBuilder';
import { ContentTypes, Context } from 'common/store/common/constants';
import { isQuestionMode, isQuestionSet } from 'common/util/shared/sharedUtils';
import { ContextState } from '../store/context/reducer';
import { ItemContext } from '../store/context/types';

const itemService = {
    buildPreviewRequest(context: ContextState, session: SessionState) {
        const languageId = i18nService.getCurrentLanguageId(session.language);
        const { currentItemContext } = context;
        let mode: PlayerParams['mode'];
        if (
            currentItemContext.contentType === ContentTypes.QUESTIONS
            || isQuestionMode()
            || currentItemContext.contentType.startsWith("content_question")
        ) {
            mode = PreviewMode.QUESTION;
        } else if (currentItemContext.contentType === ContentTypes.SECTION) {
            mode = PreviewMode.SECTION;
        } else if (currentItemContext.contentType === ContentTypes.QUESTION_SET || isQuestionSet()) {
            mode = PreviewMode.QUESTION_SET;
        }

        const requestBuilder = PlayerRequestBuilder
            .create()
            .context(Context.PREVIEW)
            .contentItemId(currentItemContext.objectId)
            .mode(PreviewMode.QUESTION)
            .selectedLanguageId(languageId)
            .previewMode(true);

        if (mode) {
            requestBuilder.mode(mode);
        }

        return requestBuilder;
    },

    getPreviewUrl(context: ContextState, session: SessionState) {
        const request = this.buildPreviewRequest(context, session);
        return request.toUrl();
    },

    getPreviewUrlWithoutCheckAnswers(context: ContextState, session: SessionState) {
        const request = this.buildPreviewRequest(context, session);
        request.viewAsCandidate();
        return request.layoutParams(
            {
                /* eslint-disable @typescript-eslint/camelcase */
                show_check_answer_button: false,
            }
        ).toUrl();
    },

    getPreviewWithCorrectAnswersUrl(context: ContextState, session: SessionState) {
        const request = this.buildPreviewRequest(context, session);
        return request
            .layoutParams(
                {
                    /* eslint-disable @typescript-eslint/camelcase */
                    show_correct_answers: true,
                }
            )
            .toUrl();
    },
    getItemReviewUrl(context: ContextState) {
        const { currentItemContext } = context;
        const isXTMEnabled = window.marketplaceProperties.ENABLE_ITEM_REVIEW_XTM;
        const itemReviewPath = isXTMEnabled ? 'item_review_xtm' : 'item_bank_default';
        let url = `${window.location.origin}/author/${itemReviewPath}/?oneWorkspaceMode=true&contentType=${currentItemContext.contentType}`;
        url += `&contentItemIds=${context.contentItemIds}&itemBankId=${context.itemBankId}&contextId=${currentItemContext.objectId}`;
        url += '&isReviewMode=true';
        return url;
    },
    getLogEventsUrl(context: ContextState) {
        const { contextId, currentItemContext: { title } } = context;
        const logObjectUrl: any = {
            contextObjectTitle: title,
            filters: { contextObjectId: contextId },
        };
        const encodedUrlParams = encodeURIComponent(JSON.stringify(logObjectUrl));

        return `/admin#log/data/${encodedUrlParams}`;
    },
    getMajorVersionsUrl(context: ContextState, versionNumber: number) {
        const { contentItemIds, contextId, itemBankId } = context;
        const contentItemId = Array.isArray(contentItemIds) ? [contextId, ...contentItemIds.filter((id) => id !== contextId)] : contentItemIds;
        let url = `/author/major_versions/?oneWorkspaceMode=true&focusView=true&contentItemIds=${contentItemId}`
        + `&compareVersionIndex=${versionNumber}&contextId=${contextId}`
        + '&isCompareMode=true&context=preview&previewType=author';
        const effectiveItemBankId = (itemBankId && itemBankId > 0)
            ? itemBankId
            : context.currentItemContext?.itemBankId;
        if (effectiveItemBankId && effectiveItemBankId > 0) {
            url += `&itemBankId=${effectiveItemBankId}`;
        }
        return url;
    },
    getAuthorUrl(context: ContextState, contentType: string) {
        const { contentItemIds, contextId } = context;
        if (contentType === ContentTypes.SECTION) {
            return `/author/author_edit/?contentType=content_qti_section&contentItemIds=${contentItemIds}&contextId=${contextId}`;
        }
        return `/author/author_edit/?contentType=content_structure_assessment&contentItemIds=${contentItemIds}&contextId=${contextId}`;
    },
    isQuestion(currentItemContext: ContextState | ItemContext) {
        return currentItemContext?.contentType?.includes(ContentTypes.QUESTION);
    },
};

export default itemService;
